/**
 * View-model helpers for the Workspaces library page.
 *
 * The page renders one flat list of workspaces (cloud projects, workspaces
 * shared with the user, and local-store workspaces) plus the folder tree.
 * Everything here is pure so it can be unit-tested without React or storage.
 */

import type { ProjectMetadata, LocalWorkspaceMetadata } from './storage';
import type { WorkspaceFolder } from './workspaceFolders';
import { getFolderById, getFolderPath } from './folderTree';

export interface LibraryWorkspace {
    id: string;
    name: string;
    /** true = IndexedDB `localWorkspaces` record (opened via loadLocalWorkspaceById). */
    isLocal: boolean;
    /** false for local-store workspaces and cloud workspaces that only exist in IndexedDB. */
    syncedToCloud: boolean;
    lastSaved: number;
    lastOpened?: number;
    createdAt?: number;
    nodeCount?: number;
    thumbnail?: string;
    folderId: string | null;
    isFavorite: boolean;
    isShared: boolean;
    sharedPermission?: 'view' | 'edit';
    /** The name of who shared it */
    sharedBy?: string;
    /** Shared as part of someone's library project (that project's id): shown in its place there, and left only with the project */
    sharedProjectId?: string;
    ownerName?: string;
    ownerId?: string;
    /** What its generations cost, in US dollars (its History's total) */
    totalCost?: number;
}

/** A cost as the library shows it: "$9.16", "<$0.01" for less than a cent, "$1,234" from a thousand on */
export const formatCost = (usd: number): string => {
    if (!(usd > 0)) return '$0';
    if (usd < 0.01) return '<$0.01';
    if (usd >= 1000) return `$${Math.round(usd).toLocaleString('en-US')}`;
    return `$${usd.toFixed(2)}`;
};

export const buildLibraryWorkspaces = (
    projects: ProjectMetadata[],
    localWorkspaces: LocalWorkspaceMetadata[],
    lastOpened: Map<string, number> = new Map(),
): LibraryWorkspace[] => {
    const byId = new Map<string, LibraryWorkspace>();

    for (const p of projects) {
        byId.set(p.id, {
            id: p.id,
            name: p.name || 'Untitled',
            isLocal: false,
            syncedToCloud: !p.isLocal,
            lastSaved: p.lastSaved || 0,
            lastOpened: lastOpened.get(p.id),
            createdAt: p.createdAt,
            nodeCount: p.nodeCount,
            thumbnail: p.thumbnail,
            // A shared workspace has no place in this person's library, unless it came with a shared project
            folderId: p.isShared && !p.sharedProjectId ? null : (p.folderId ?? null),
            isFavorite: !p.isShared && !!p.isFavorite,
            isShared: !!p.isShared,
            sharedPermission: p.sharedPermission,
            sharedBy: p.sharedBy,
            ...(p.isShared && p.sharedProjectId ? { sharedProjectId: p.sharedProjectId } : {}),
            ownerId: p.ownerId,
            ownerName: p.ownerName,
            totalCost: p.totalCost,
        });
    }

    for (const w of localWorkspaces) {
        if (byId.has(w.id)) continue;
        byId.set(w.id, {
            id: w.id,
            name: w.name || 'Untitled',
            isLocal: true,
            syncedToCloud: false,
            lastSaved: w.updatedAt || w.createdAt || 0,
            lastOpened: lastOpened.get(w.id),
            createdAt: w.createdAt,
            nodeCount: w.nodeCount,
            thumbnail: w.thumbnail,
            folderId: w.folderId ?? null,
            isFavorite: !!w.isFavorite,
            isShared: false,
            totalCost: w.totalCost,
        });
    }

    return Array.from(byId.values()).sort((a, b) => b.lastSaved - a.lastSaved);
};

// ── Location (what the page is looking at) ───────────────────────────────────

export type LibraryView = 'library' | 'recent' | 'starred' | 'shared';

export interface LibraryLocation {
    view: LibraryView;
    /** Only meaningful for view 'library': null = the root (projects + unfiled workspaces). */
    folderId: string | null;
    /** Ancestor chain (root project first, the folder itself last) captured when stored, so a deleted folder can fall back to its nearest surviving ancestor. */
    path?: string[];
}

export const ROOT_LOCATION: LibraryLocation = { view: 'library', folderId: null };

const locationKey = (uid: string) => `ns-lib:location:${uid}`;
const EXPANDED_KEY = 'ns-lib:expanded';
const PREFS_KEY = 'ns-lib:prefs';

const safeStorage = (kind: 'session' | 'local'): Storage | null => {
    try {
        const s = kind === 'session' ? window.sessionStorage : window.localStorage;
        return s || null;
    } catch {
        return null;
    }
};

export const readStoredLocation = (uid: string): LibraryLocation | null => {
    const s = safeStorage('session');
    if (!s) return null;
    try {
        const raw = s.getItem(locationKey(uid));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const view: LibraryView = ['library', 'recent', 'starred', 'shared'].includes(parsed.view) ? parsed.view : 'library';
        const path = Array.isArray(parsed.path) ? parsed.path.filter((x: unknown) => typeof x === 'string') : undefined;
        return { view, folderId: typeof parsed.folderId === 'string' ? parsed.folderId : null, path };
    } catch {
        return null;
    }
};

/** Persist the location; `folders` lets the ancestor chain be captured for later fallback. */
export const writeStoredLocation = (uid: string, location: LibraryLocation, folders?: WorkspaceFolder[]): void => {
    const s = safeStorage('session');
    if (!s) return;
    try {
        const path = location.folderId && folders ? getFolderPath(folders, location.folderId).map(f => f.id) : location.path;
        s.setItem(locationKey(uid), JSON.stringify({ view: location.view, folderId: location.folderId, path }));
    } catch {
        /* quota / private mode: ignore */
    }
};

export const clearStoredLocations = (): void => {
    const s = safeStorage('session');
    if (!s) return;
    try {
        const keys: string[] = [];
        for (let i = 0; i < s.length; i++) {
            const k = s.key(i);
            if (k && k.startsWith('ns-lib:location:')) keys.push(k);
        }
        keys.forEach(k => s.removeItem(k));
    } catch {
        /* ignore */
    }
};

/**
 * A stored folder may have been deleted on another device: fall back to the
 * nearest ancestor that still exists, then to the root.
 */
export const resolveLocation = (location: LibraryLocation, folders: WorkspaceFolder[]): LibraryLocation => {
    if (location.view !== 'library' || location.folderId === null) return { view: location.view, folderId: null };
    if (getFolderById(folders, location.folderId)) return { view: 'library', folderId: location.folderId };
    // The folder is gone: walk the stored ancestor chain from the deepest survivor.
    const chain = location.path || [];
    for (let i = chain.length - 1; i >= 0; i--) {
        if (chain[i] !== location.folderId && getFolderById(folders, chain[i])) return { view: 'library', folderId: chain[i] };
    }
    return ROOT_LOCATION;
};

export const readExpandedFolderIds = (): Set<string> => {
    const s = safeStorage('local');
    if (!s) return new Set();
    try {
        const raw = s.getItem(EXPANDED_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr.filter((x: unknown) => typeof x === 'string') : []);
    } catch {
        return new Set();
    }
};

export const writeExpandedFolderIds = (ids: Set<string>): void => {
    const s = safeStorage('local');
    if (!s) return;
    try {
        s.setItem(EXPANDED_KEY, JSON.stringify(Array.from(ids)));
    } catch {
        /* ignore */
    }
};

export type LibrarySort = 'edited' | 'name' | 'created';

export interface LibraryPrefs {
    sort: LibrarySort;
    /** What this person pinned to the top of the sidebar: project, folder and workspace ids, on this device */
    pinned: string[];
}

export const readLibraryPrefs = (): LibraryPrefs => {
    const s = safeStorage('local');
    const fallback: LibraryPrefs = { sort: 'edited', pinned: [] };
    if (!s) return fallback;
    try {
        const raw = s.getItem(PREFS_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return {
            sort: ['edited', 'name', 'created'].includes(parsed.sort) ? parsed.sort : 'edited',
            pinned: Array.isArray(parsed.pinned) ? parsed.pinned.filter((id: unknown): id is string => typeof id === 'string').slice(0, 12) : [],
        };
    } catch {
        return fallback;
    }
};

export const writeLibraryPrefs = (prefs: LibraryPrefs): void => {
    const s = safeStorage('local');
    if (!s) return;
    try {
        s.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
        /* ignore */
    }
};

// ── Sorting, searching, formatting ───────────────────────────────────────────

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const sortWorkspaces = (items: LibraryWorkspace[], sort: LibrarySort): LibraryWorkspace[] => {
    const copy = [...items];
    switch (sort) {
        case 'name':
            return copy.sort((a, b) => collator.compare(a.name, b.name));
        case 'created':
            return copy.sort((a, b) => (b.createdAt ?? b.lastSaved) - (a.createdAt ?? a.lastSaved));
        default:
            return copy.sort((a, b) => b.lastSaved - a.lastSaved);
    }
};

/** Last opened on this device, newest first; workspaces never opened here are excluded. */
export const recentWorkspaces = (items: LibraryWorkspace[], limit = 8): LibraryWorkspace[] =>
    items
        .filter(w => typeof w.lastOpened === 'number')
        .sort((a, b) => (b.lastOpened as number) - (a.lastOpened as number))
        .slice(0, limit);

export const matchesQuery = (name: string, query: string): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const n = name.toLowerCase();
    return q.split(/\s+/).every(term => n.includes(term));
};

/** "Art direction / Characters" for the breadcrumb-style location column. */
export const describeLocation = (folders: WorkspaceFolder[], workspace: Pick<LibraryWorkspace, 'folderId' | 'isShared' | 'sharedBy' | 'sharedProjectId'>): string => {
    if (workspace.isShared) {
        // In a shared project: where it is in that project, when the project is known here
        const path = workspace.sharedProjectId && workspace.folderId ? getFolderPath(folders, workspace.folderId) : [];
        if (path.length) return path.map(f => f.name).join(' / ');
        return workspace.sharedBy ? `Shared by ${workspace.sharedBy}` : 'Shared with me';
    }
    if (!workspace.folderId) return 'Not in a project';
    const path = getFolderPath(folders, workspace.folderId);
    return path.length ? path.map(f => f.name).join(' / ') : 'Not in a project';
};

export const formatRelativeTime = (timestamp: number | undefined, now: number = Date.now()): string => {
    if (!timestamp) return '';
    const diff = Math.max(0, now - timestamp);
    const minutes = diff / 60000;
    const hours = minutes / 60;
    const days = hours / 24;
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${Math.floor(minutes)} min ago`;
    if (hours < 24) return `${Math.floor(hours)} h ago`;
    if (days < 7) return `${Math.floor(days)} d ago`;
    return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: days > 365 ? 'numeric' : undefined });
};

export const pluralize = (n: number, one: string, many: string = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

const nameKey = (name: string): string => name.trim().toLowerCase();

/**
 * Whether a workspace file found in the local folder is one this device already has (`known`):
 * by its id, or by its name when the file has none. Such a file is not brought in again when the
 * folder is chosen: the copy here may be newer than the folder's.
 */
export const knownWorkspaceCheck = (known: Array<{ id: string; name: string }>) => {
    const ids = new Set(known.map(workspace => workspace.id));
    const names = new Set(known.map(workspace => nameKey(workspace.name || '')));
    return (projectId: string | undefined, name: string): boolean => (projectId ? ids.has(projectId) : names.has(nameKey(name)));
};
